import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { EASE, MaskedLines, Reveal } from "@/components/aklive/motion";
import { PlaceholderBlock } from "@/components/aklive/PlaceholderBlock";

const SERVICES = [
  {
    slug: "brand-identity",
    need: "Launching something?",
    name: "Brand Identity",
    outcome: "Build recognition and trust from the beginning.",
    creates: "Logo, colors, typography, visual language and practical guidelines.",
    steps: ["Understand the business", "Research the market", "Define the direction", "Build the identity"],
    related: ["Vamika", "Salted Social", "Meducan", "Puretive"],
    label: "[BRAND SYSTEM — 4:5]",
  },
  {
    slug: "pitch-decks",
    need: "Raising or pitching?",
    name: "Pitch Decks and Presentations",
    outcome: "Make the opportunity clear and credible.",
    creates: "Investor decks, fundraising presentations, sales decks, partnership decks and company profiles.",
    steps: ["Understand the story", "Simplify the information", "Create a persuasive visual flow"],
    related: ["Midas Capital", "VNX", "Salted Social"],
    label: "[PITCH DECK SPREAD — 4:5]",
  },
  {
    slug: "social-content",
    need: "Growing your communication?",
    name: "Social and Content Systems",
    outcome: "Create a system your team can use repeatedly.",
    creates: "Content categories, post templates, carousel systems, campaigns and launch assets.",
    steps: ["Define useful topics", "Establish one visual language", "Create reusable templates"],
    related: ["Proof of Talk", "Harmony", "0x.Art", "Salted Social"],
    label: "[CONTENT SYSTEM — 4:5]",
  },
  {
    slug: "event-branding",
    need: "Creating an experience?",
    name: "Event and Conference Branding",
    outcome: "Build a cohesive environment people remember.",
    creates: "Event identity, stage graphics, signage, wayfinding, speaker assets and sponsor assets.",
    steps: ["Understand the event", "Create the system", "Apply it across digital, venue and navigation touchpoints"],
    related: ["Proof of Talk"],
    label: "[EVENT BRANDING — 4:5]",
  },
  {
    slug: "packaging",
    need: "Taking a product to market?",
    name: "Packaging Design",
    outcome: "Shape perception before the product is opened.",
    creates: "Packaging, labels, product systems, guidelines and presentation mockups.",
    steps: ["Study the product and market", "Create shelf impact", "Build consistency across variants"],
    related: ["Puretive", "92 Degree Coffee"],
    label: "[PACKAGING CLOSE-UP — 4:5]",
  },
  {
    slug: "website-design",
    need: "Building your online presence?",
    name: "Website Design",
    outcome: "Help visitors understand the business and take action.",
    creates: "Business websites, startup websites, landing pages and conversion-focused user experiences.",
    steps: ["Understand user questions", "Structure the information", "Design for clarity and action"],
    related: ["Impelsys", "VNX"],
    label: "[WEBSITE MOCKUP — 4:5]",
  },
];

const Detail = ({ service }) => (
  <div className="grid gap-8 pb-10 md:grid-cols-2">
    <div className="space-y-8">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink2">What we create</p>
        <p className="mt-3 text-base leading-relaxed text-ink">{service.creates}</p>
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink2">How it works</p>
        <p className="mt-3 text-base leading-relaxed text-ink">
          {service.steps.map((step, i) => (
            <span key={step}>
              {step}
              {i < service.steps.length - 1 && <span className="px-2 text-ember" aria-hidden="true">→</span>}
            </span>
          ))}
        </p>
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink2">Related work</p>
        <p className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
          {service.related.map((r) => (
            <Link
              key={r}
              to="/work"
              data-testid={`related-${service.slug}-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="link-underline text-base font-medium text-ink hover:text-ember"
            >
              {r}
            </Link>
          ))}
        </p>
      </div>
    </div>
    <PlaceholderBlock label={service.label.replace("4:5", "3:2")} ratio="3 / 2" className="md:hidden" />
  </div>
);

export const ServicesIndex = () => {
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(0);
  const [visual, setVisual] = useState(0);

  return (
    <section className="py-24 md:py-40" data-testid="services-index-section">
      <div className="container-ak grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <h2 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              <MaskedLines
                lines={[
                  <span key="l1">
                    Services<span className="text-ember">.</span>
                  </span>,
                ]}
              />
            </h2>
            <Reveal delay={0.15} className="mt-6">
              <p className="max-w-[380px] text-base leading-relaxed text-ink2 md:text-lg">
                Start with the business moment. The right service follows.
              </p>
            </Reveal>
            <Reveal delay={0.25} className="mt-10 hidden lg:block">
              <div aria-hidden="true" className="relative max-w-[360px]" style={{ aspectRatio: "4 / 5" }}>
                <AnimatePresence>
                  <motion.div
                    key={visual}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: reduce ? 0 : 0.4, ease: EASE }}
                    className="absolute inset-0"
                    data-testid="services-index-visual"
                  >
                    <PlaceholderBlock label={SERVICES[visual].label} fill />
                  </motion.div>
                </AnimatePresence>
              </div>
            </Reveal>
            <Reveal delay={0.3} className="mt-10 hidden lg:block">
              <Link
                to="/work"
                data-testid="services-index-work-link"
                className="link-underline inline-flex items-center gap-2 font-display text-base font-bold tracking-tight text-ink hover:text-ember"
              >
                View Our Work <span aria-hidden="true">→</span>
              </Link>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-8 lg:col-start-5">
          <Reveal>
            <div className="border-t border-line" data-testid="services-full-index">
              {SERVICES.map((s, i) => {
                const isOpen = open === i;
                return (
                  <div key={s.slug} className="border-b border-line">
                    <button
                      type="button"
                      data-testid={`service-toggle-${s.slug}`}
                      aria-expanded={isOpen}
                      onClick={() => {
                        setOpen(isOpen ? -1 : i);
                        setVisual(i);
                      }}
                      onMouseEnter={() => setVisual(i)}
                      onFocus={() => setVisual(i)}
                      className="group flex w-full items-baseline justify-between gap-6 py-6 text-left md:py-8"
                    >
                      <span className="min-w-0">
                        <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ember md:text-xs">
                          {s.need}
                        </span>
                        <span
                          className={`mt-2 block font-display text-2xl font-bold tracking-tight transition-colors duration-300 md:text-3xl ${
                            isOpen ? "text-ember" : "text-ink group-hover:text-ember"
                          }`}
                        >
                          {s.name}
                        </span>
                        <span className="mt-2 block max-w-[520px] text-sm leading-relaxed text-ink2 md:text-base">
                          {s.outcome}
                        </span>
                      </span>
                      <span
                        aria-hidden="true"
                        className={`self-center text-xl text-ink2 transition-transform duration-300 md:text-2xl ${
                          isOpen ? "rotate-45" : ""
                        }`}
                      >
                        +
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          key="panel"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: reduce ? 0 : 0.45, ease: EASE }}
                          className="overflow-hidden"
                          data-testid={`service-panel-${s.slug}`}
                        >
                          <Detail service={s} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </Reveal>
          <Reveal className="mt-10 lg:hidden">
            <Link
              to="/work"
              data-testid="services-index-work-link-mobile"
              className="link-underline inline-flex min-h-[44px] items-center gap-2 font-display text-base font-bold tracking-tight text-ink hover:text-ember"
            >
              View Our Work <span aria-hidden="true">→</span>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
